/**
 * Menu Configuration
 *
 * Contains:
 * 1. sideNavMenus - Main navigation bar icon menus (SideNav.vue)
 * 2. pageData - Drawer accordion menus and route configuration (routeApp.js, SideNavDrawer.vue)
 *
 * NOTE: 本檔案逐步遷移至統一配置系統 (remotes/index.ts)
 * 新增頁面請優先在 remotes/*.ts 中配置
 */

// ============================================
// Import from Unified Config System
// ============================================

import {
    remoteModules as generatedRemoteModules,
    generatedSideNavMenus,
} from './remotes';

// ============================================
// Type Definitions
// ============================================

/**
 * Menu item for side navigation bar
 */
export interface SideNavMenuItem {
    /** Route path */
    path: string;
    /** Display title (Chinese) */
    title: string;
    /** Icon class (icofont) */
    icon: string;
}

/**
 * Side navigation menus grouped by module
 */
export interface SideNavMenus {
    /** Operation module menus (om Remote) */
    operation: SideNavMenuItem[];
    /** Quotation/Planner module menus (om Remote) */
    quotation: SideNavMenuItem[];
    /** ESS module menus (om + management Remote) */
    ess: SideNavMenuItem[];
    /** War room module menus (om Remote) */
    warroom: SideNavMenuItem[];
    /** Workflow module menus (afu Remote) */
    workflow: SideNavMenuItem[];
    /** Report module menus (本地 report) */
    report: SideNavMenuItem[];
    /** Management/Admin module menus */
    management: SideNavMenuItem[];
    /** Big screen module menus */
    bigscreen: SideNavMenuItem[];
    /** Tools module menus */
    tools: SideNavMenuItem[];
    /** Chat/Communication module menus */
    chat: SideNavMenuItem[];
    /** HR module menus (hr Remote) */
    hr: SideNavMenuItem[];
    /** ESS Planner module menus (essPlanner Remote) */
    essPlanner: SideNavMenuItem[];
    /** Organization module menus (om Remote) */
    org: SideNavMenuItem[];
    /** Files module menus */
    files: SideNavMenuItem[];
    /** Test module menus */
    test: SideNavMenuItem[];
    /** Placeholder module menus (shares test items) */
    placeholder: SideNavMenuItem[];
}

/**
 * Route metadata for page items
 */
export interface PageMeta {
    /** Whether the page requires authentication */
    requiresAuth?: boolean;
    /** Required roles to access the page */
    roles?: string[];
}

/**
 * Page item configuration for routing
 */
export interface PageItem {
    /** Directory under the app where the component is located */
    dir: string;
    /** Display title */
    title: string;
    /** Component/page name */
    page: string;
    /** Optional route name override */
    name?: string;
    /** Route metadata */
    meta?: PageMeta;
}

/**
 * Drawer menu item (used for navigation and special actions)
 */
export interface DrawerMenuItem {
    /** Directory under the app where the component is located */
    dir?: string;
    /** Display title */
    title: string;
    /** Component/page name */
    page?: string;
    /** Optional route name override */
    name?: string;
    /** Route metadata */
    meta?: PageMeta;
    /** Special action (e.g., 'logout') */
    action?: string;
    /** Icon class for action items */
    icon?: string;
}

/**
 * Navigation group containing multiple menu items
 */
export interface NavGroup {
    /** Group display title */
    title: string;
    /** Group identifier name */
    name: string;
    /** Menu items in this group */
    items: DrawerMenuItem[];
}

/**
 * Page data configuration object
 */
export interface PageData {
    /** Main navigation menu groups */
    nav: NavGroup[];
    /** Test/development menu groups */
    test: NavGroup[];
    /** Drawer page configurations */
    drawer: PageItem[];
    /** General page configurations */
    page: PageItem[];
}

// ============================================
// Remote Module Configuration (Module Federation)
// ============================================

/**
 * 遠端模組配置
 * 用於 MultiViewManager 動態載入 Module Federation 遠端組件
 */
export interface RemoteModuleConfig {
    /** 模組名稱 (對應 import 路徑) */
    name: string;
    /** 遠端入口 URL */
    url: string;
    /** 暴露的組件列表 */
    exposes: string[];
}

/**
 * 遠端模組映射
 * key: 目錄名 (用於 routeName 解析)
 * value: 暴露的組件名稱列表
 *
 * NOTE: 自動從 remotes/*.ts 生成
 */
export const remoteModules: Record<string, string[]> = generatedRemoteModules;

/**
 * 取得遠端模組的動態 import 函數
 *
 * NOTE: 由於 Vite 需要靜態分析 import 路徑，這裡必須使用硬編碼的 switch/case。
 * 新增頁面時需要同時更新：
 * 1. remotes/*.ts 中的頁面配置
 * 2. 此函數中對應的 import 語句
 *
 * 未來考慮使用 vite-plugin-dynamic-import 或其他方案自動化。
 */
export function getRemoteModuleLoader(dir: string, page: string): (() => Promise<any>) | null {
    // ===== Dev 模式：本地優先（hot-reload 開發用） =====
    if (import.meta.env.DEV) {
        return getLocalPageLoader(dir, page);
    }

    // ===== Prod 模式：遠端優先（已同步至 Remote 的正式版） =====
    if (remoteModules[dir]?.includes(page)) {
        // 所有 Remote 都使用 lazy loader（獨立 chunk，按需載入 remoteEntry.js）
        const lazyLoader = getLazyRemoteLoader(dir, page);
        if (lazyLoader) return lazyLoader;
    }

    // ===== Fallback：本地版（尚未同步到遠端的頁面） =====
    return getLocalPageLoader(dir, page);
}

/**
 * 延遲載入的 Remote 模組
 *
 * 所有 Remote 的 import 放在獨立檔案中（lazyRemoteLoaders/），
 * Vite 會將每個檔案打包為獨立 chunk。
 * 只有在用戶實際導航到對應頁面時，才會載入該 Remote 的 remoteEntry.js。
 *
 * 新增 remote 頁面：在對應的 lazyRemoteLoaders/{remote}.ts 中加一行即可。
 */
const lazyRemoteImporters: Record<string, () => Promise<{ loaders: Record<string, () => Promise<any>> }>> = {
    om: () => import('./lazyRemoteLoaders/om'),
    dispatch: () => import('./lazyRemoteLoaders/om'), // dispatch 映射到 om remote
    bigscreen: () => import('./lazyRemoteLoaders/bigscreen'),
    hr: () => import('./lazyRemoteLoaders/hr'),
    afu: () => import('./lazyRemoteLoaders/afu'),
    essPlanner: () => import('./lazyRemoteLoaders/essPlanner'),
    management: () => import('./lazyRemoteLoaders/management'),
};

function getLazyRemoteLoader(dir: string, page: string): (() => Promise<any>) | null {
    const importer = lazyRemoteImporters[dir];
    if (!importer) return null;
    return () => importer().then(m => {
        const loader = m.loaders[page];
        if (!loader) throw new Error(`[LazyRemote] ${dir}/${page} not found`);
        return loader();
    });
}

/**
 * 本地頁面載入器（import.meta.glob 自動發現）
 *
 * 新增本地頁面只需放到 src/views/{dir}/ 目錄，不用改這裡。
 * key 格式: /src/views/{dir}/{Page}.vue
 */
const localViews: Record<string, () => Promise<any>> = import.meta.glob('/src/views/*/*.vue');

// Dev-only：從 om remote 遷移到 core 的頁面（prod 仍走 remote 避免打包膨脹）
const devLocalViews: Record<string, () => Promise<any>> = import.meta.env.DEV
    ? import.meta.glob('/src/views/warroom/*.vue')
    : {};

// om dir → core filesystem dir 的映射（dev-only，prod 走 remote）
const devDirMap: Record<string, string> = {
    'om': 'warroom',
};

function getLocalPageLoader(dir: string, page: string): (() => Promise<any>) | null {
    // 標準本地頁面：dir 和 filesystem 目錄一致
    const key = `/src/views/${dir}/${page}.vue`;
    if (localViews[key]) return localViews[key];

    // Dev-only：om 等 remote 頁面走本地
    if (import.meta.env.DEV && devDirMap[dir]) {
        const devKey = `/src/views/${devDirMap[dir]}/${page}.vue`;
        if (devLocalViews[devKey]) return devLocalViews[devKey];
    }

    return null;
}

// ============================================
// Main Navigation Menus (SideNav)
// ============================================
// IMPORTANT: 圖示使用 icofont (https://icofont.com/)
// 請勿使用 duotone 類型的圖示，僅使用基本線條圖示
// ============================================

export const sideNavMenus: SideNavMenus = {
    // 維運模式子選單 (om Remote)
    // 路徑格式: /[remote名稱]/[組件名稱]
    operation: [
        { path: '/om/FocusField', title: '焦點案場', icon: 'icofont-star' },
        { path: '/om/SystemOverview', title: '系統看板', icon: 'icofont-dashboard-web' },
        { path: '/om/EquipmentOverview', title: '設備總覽', icon: 'icofont-listing-box' },
        { path: '/om/WorkOrderList', title: '工單管理', icon: 'icofont-repair' },
        { path: '/om/MaintenanceBoard', title: '維運看板', icon: 'icofont-tasks-alt' },
        { path: '/om/EventsList', title: '訊息匯整', icon: 'icofont-email' },
        { path: '/om/AlarmList', title: '告警列表', icon: 'icofont-alarm' },
        { path: '/om/FieldSelector', title: '案場選擇', icon: 'icofont-location-pin' },
        { path: '/inventory/SparePartsList', title: '備品管理', icon: 'icofont-box' },
    ],
    // essPlanner Remote - 儲能報價系統（子選單）
    quotation: [
        { path: '/essPlanner/Home', title: '案件規劃', icon: 'icofont-battery-full' },
        { path: '/essPlanner/ProjectList', title: '專案列表', icon: 'icofont-list' },
        { path: '/essPlanner/QuotationList', title: '報價列表', icon: 'icofont-paper' },
        { path: '/essPlanner/CostSettings', title: '成本設定', icon: 'icofont-money' },
        { path: '/essPlanner/RevenueAnalysis', title: '收益分析', icon: 'icofont-chart-line' },
        { path: '/essPlanner/SheetEditor', title: '表格編輯', icon: 'icofont-table' },
    ],
    // 儲能控制（子選單）
    ess: [
        { path: '/om/ESSControl', title: '儲能控制', icon: 'icofont-ui-power' },
        { path: '/om/ESSMonitor', title: '儲能監控', icon: 'icofont-chart-histogram' },
        { path: '/management/EssStrategySettings', title: '策略設定', icon: 'icofont-cube' },
        { path: '/management/TaipowerPriceSettings', title: '電價時段', icon: 'icofont-clock-time' },
    ],
    // om Remote - 戰情室（子選單）
    warroom: [
        { path: '/om/VisualizationDashboard', title: '戰情看板', icon: 'icofont-chart-pie' },
        { path: '/om/RiskDashboard', title: '風險看板', icon: 'icofont-warning' },
        { path: '/om/EventsDashboard', title: '事件看板', icon: 'icofont-bell' },
        { path: '/om/EmergencyDashboard', title: '應急響應', icon: 'icofont-fire-alt' },
        { path: '/om/FinancialDashboard', title: '收支看板', icon: 'icofont-money' },
    ],
    // afu Remote - 流程（子選單）
    workflow: [
        { path: '/afu/Cases', title: '案件', icon: 'icofont-clip' },
        { path: '/afu/Tasks', title: '任務', icon: 'icofont-check-circled' },
        { path: '/afu/CreateCase', title: '建立案件', icon: 'icofont-plus-circle' },
        { path: '/afu/CaseFlow', title: '流程看板', icon: 'icofont-chart-flow-1' },
        { path: '/afu/MyTasks', title: '我的任務', icon: 'icofont-tasks' },
        { path: '/afu/TaskSchedule', title: '任務排程', icon: 'icofont-calendar' },
    ],
    // 報表（子選單）- om Remote
    report: [
        { path: '/om/EnergyStatistics', title: '數據趨勢', icon: 'icofont-chart-line' },
        { path: '/om/EnergyGeneration', title: '發電統計', icon: 'icofont-chart-bar-graph' },
        { path: '/om/ReportList', title: '報表管理', icon: 'icofont-file-text' },
        { path: '/om/BillList', title: '電費帳單', icon: 'icofont-ticket' },
        { path: '/report/MaintenanceFinanceList', title: '收支明細', icon: 'icofont-money' },
    ],
    // management Remote - 管理（子選單）
    management: [
        { path: '/management/AlarmSettings', title: '告警設定', icon: 'icofont-gear' },
        { path: '/management/CreateOrganization', title: '新增組織', icon: 'icofont-people' },
        { path: '/management/TDengineSchemaGenerator', title: 'TDEngine', icon: 'icofont-database-locked' },
        { path: '/management/EscCommandHistory', title: '指令歷程', icon: 'icofont-listine-dots' },
        { path: '/management/ReplaceUserId', title: '用戶遷移', icon: 'icofont-exchange' },
        { path: '/management/CronMonitor', title: '排程監控', icon: 'icofont-wall-clock' },
        { path: '/management/FindEquipment', title: '設備查詢', icon: 'icofont-search-1' },
        { path: '/management/MQTTCommand', title: 'MQTT', icon: 'icofont-send-mail' },
        { path: '/management/MqttServiceStatus', title: 'MQTT Service', icon: 'icofont-pulse' },
        { path: '/management/OrganizationList', title: '組織列表', icon: 'icofont-company' },
        { path: '/management/PermissionTest', title: '權限測試', icon: 'icofont-test-tube-alt' },
        // 權限管理（core 本地頁面）
        { path: '/admin/PagePermissionByOrg', title: '按組織管理', icon: 'icofont-people' },
        { path: '/admin/PagePermissionByPage', title: '按頁面管理', icon: 'icofont-lock' },
    ],
    // 大屏（子選單）- bigscreen Remote
    bigscreen: [
        { path: '/bigscreen/VisualizationScreen', title: '大屏列表', icon: 'icofont-monitor' },
        { path: '/bigscreen/Settings', title: '大屏設定', icon: 'icofont-ui-settings' }
    ],
    // 工具（子選單）- 本地 tools
    tools: [
        { path: '/tools/UserUIList', title: '工具列表', icon: 'icofont-numbered' },
        // 動態項目：釘選的工具由 SideNavDrawer 載入
        // UserUIRenderer 不需要獨立選單項目，由工具列表點擊進入
    ],
    // 通訊（子選單）- 本地 chat
    chat: [
        { path: '/chat/GroupsView', title: '群組管理', icon: 'icofont-users-alt-2' },
        { path: '/chat/ChatHistory', title: '聊天歷史', icon: 'icofont-history' },
        { path: '/chat/ChatSettings', title: '通訊設定', icon: 'icofont-ui-settings' }
    ],
    // hr Remote - 人資系統
    hr: [
        { path: '/hr/Dashboard', title: '人資首頁', icon: 'icofont-dashboard-web' },
        { path: '/hr/MySchedule', title: '我的班表', icon: 'icofont-ui-calendar' },
        { path: '/hr/FormApplication', title: '表單申請', icon: 'icofont-ui-file' },
        { path: '/hr/NeedSignOff', title: '待我簽核', icon: 'icofont-pencil-alt-2' },
        { path: '/hr/StaffList', title: '員工名冊', icon: 'icofont-users' },
        { path: '/hr/Department', title: '部門管理', icon: 'icofont-company' },
    ],
    // essPlanner Remote - 已合併到 quotation 選單
    essPlanner: [],
    // 組織管理（om Remote）
    org: [
        { path: '/om/OrganizationDetail', title: '組織設定', icon: 'icofont-building-alt' },
        { path: '/om/OrganizationMember', title: '成員管理', icon: 'icofont-users-alt-2' },
        { path: '/om/OrganizationInvite', title: '邀請管理', icon: 'icofont-envelope' },
        { path: '/om/OrganizationField', title: '組織案場', icon: 'icofont-building' },
        { path: '/om/OrganizationJoin', title: '加入組織', icon: 'icofont-login' },
    ],
    // 檔案（預留）
    files: [],
    // 測試（子選單）- management Remote
    test: [
        { path: '/management/MySQLTest', title: 'MySQL', icon: 'icofont-database-add' },
        { path: '/management/UniverPoc', title: 'POC', icon: 'icofont-table' },
        { path: '/management/UniverFin', title: 'FIN', icon: 'icofont-file-excel' },
    ],
    // 預留（子選單）- 與 test 相同
    placeholder: [
        { path: '/management/MySQLTest', title: 'MySQL', icon: 'icofont-database-add' },
        { path: '/management/UniverPoc', title: 'POC', icon: 'icofont-table' },
        { path: '/management/UniverFin', title: 'FIN', icon: 'icofont-file-excel' },
    ]
};

// ============================================
// Drawer Accordion Menus and Route Configuration (pageData)
// ============================================

export const pageData: PageData = {
    'nav': [
        {
            title: '戰情總覽',
            name: 'monitoring',
            items: [
                {dir: 'patrol', title: '系統看板', page: 'SystemOverview', meta: {"requiresAuth": true}},
            ]
        },
        {
            title: '調度控制',
            name: 'control',
            items: [
                {dir: 'dispatch', title: 'AI 智慧決策輔助', page: 'SmartDecision', meta: {"requiresAuth": true}},
                {dir: 'dispatch', title: '儲能系統設定', page: 'EnergyStorageSettings', meta: {"requiresAuth": true}},
                {dir: 'dispatch', title: '能源調度策略', page: 'calcStrategy', meta: {"requiresAuth": true}},
                {dir: 'dispatch', title: '發電設備遠端控制', page: 'RemoteControl', meta: {"requiresAuth": true}},
                {dir: 'dispatch', title: '發電預測', page: 'pvToday', meta: {"requiresAuth": true}},
                {dir: 'dispatch', title: '負載預測', page: 'LoadPredictor', meta: {"requiresAuth": true}},
            ]
        },
        {
            title: '維運管理',
            name: 'management',
            items: [
                {dir: 'manage', title: '訊息匯整', page: 'EventsList', meta: {"requiresAuth": true}},
                {dir: 'dispatch', title: '工單管理', page: 'WorkOrderList', meta: {"requiresAuth": true}},
                {dir: 'dispatch', title: '工作回報', page: 'WorkReport', meta: {"requiresAuth": true}},
                {dir: 'dispatch', title: '定期保養計劃', page: 'MaintainPlan', meta: {"requiresAuth": true}},
            ]
        },
        {
            title: '報表',
            name: 'report',
            items: [
                {dir: 'report', title: '數據趨勢', page: 'EnergyStatistics', meta: {"requiresAuth": true}},
                {dir: 'report', title: '發電統計', page: 'EnergyGeneration', meta: {"requiresAuth": true}},
                {dir: 'report', title: '報表管理', page: 'ReportList', name: 'report_ReportList', meta: {"requiresAuth": true}},
                {dir: 'report', title: '電費帳單', page: 'BillList', meta: {"requiresAuth": true}},
            ]
        },
        {
            title: '設定',
            name: 'manage',
            items: [
                {dir: 'tools', title: '用戶工具', page: 'UserUIList', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '組織管理', page: 'OrganizationDetail', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '成員管理', page: 'OrganizationMember', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '案場資料列表', page: 'ContractList', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '新增案場', page: 'CreateField', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '權限檢查', page: 'PermissionTest', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '個人資料', page: 'UserProfile', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '通知設定', page: 'NotificationSettings', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '資料匯入', page: 'ImportFieldData', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '能源策略設定', page: 'EssStrategySettings', meta: {"requiresAuth": true}},
                {dir: 'system', title: '資料狀態', page: 'DataStatus', meta: {"requiresAuth": true}},
                {dir: 'views', title: '使用手冊', page: 'UserManual', meta: {"requiresAuth": true}},
                {action: 'logout', title: '登出', icon: 'icofont-logout', meta: {"requiresAuth": true}},
            ]
        },
    ],
    'test': [
        {
            title: '資料庫工具',
            name: 'dbTools',
            items: [
                {dir: 'manage', title: 'MySQL', page: 'MySQLTest', meta: {"requiresAuth": true}},
                {dir: 'manage', title: 'TD 產生器', page: 'TDengineSchemaGenerator', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '組織資料遷移', page: 'OrganizationMigration', meta: {"requiresAuth": true, "roles": ["admin"]}},
                {dir: 'manage', title: '關係資料遷移', page: 'RelationshipMigration', meta: {"requiresAuth": true, "roles": ["admin"]}},
                {dir: 'manage', title: '案場座標遷移', page: 'FieldLocationMigration', meta: {"requiresAuth": true, "roles": ["admin"]}},
                {dir: 'manage', title: '權限資料遷移', page: 'PermissionMigration', meta: {"requiresAuth": true, "roles": ["admin"]}},
                {dir: 'manage', title: '用戶資料修復', page: 'UserProfileFixer', meta: {"requiresAuth": true, "roles": ["admin"]}},
            ]
        },
        {
            title: '測試工具',
            name: 'testTools',
            items: [
                {dir: 'report', title: 'CronMonitor', page: 'CronMonitor', meta: {"requiresAuth": true}},
                {dir: 'dispatch', title: 'Workflow', page: 'WorkflowList', meta: {"requiresAuth": true}},
                {dir: 'manage', title: 'Find Equmitment', page: 'FindEquipment', meta: {"requiresAuth": true}},
                {dir: 'test', title: '事件服務測試', page: 'EventTestPage', meta: {"requiresAuth": true}},
                {dir: 'components', title: 'Markdown 測試', page: 'SimpleMarkdownTest', meta: {"requiresAuth": true}},
                {dir: 'manage', title: 'MQTT Command', page: 'MQTTCommand', meta: {"requiresAuth": true}},
                {dir: 'manage', title: '新增組織', page: 'CreateOrganization', meta: {"requiresAuth": true}},
            ]
        },
    ],
    'drawer': [
        {dir: 'drawer', title: '事件詳情', page: 'EventDetail'},
        {dir: 'drawer', title: '風險事件詳情', page: 'RiskEventDetail'},
        {dir: 'drawer', title: '圖表', page: 'Chart'},
        {dir: 'drawer', title: '任務排程', page: 'CronItemDetail'},
        {dir: 'drawer', title: '保固詳情', page: 'WarrantyDetail'},
        {dir: 'drawer', title: '保養記錄', page: 'CreateMaintainRecord'},
        {dir: 'drawer', title: '案件保養詳細', page: 'caseMaintenance'},
        {dir: 'drawer', title: '報修單詳細', page:'RepairDetail'},
        {dir: 'drawer', title: '履歷新增', page: 'FieldEventCreate'},
        {dir: 'drawer', title: '案場編輯', page: 'FieldDetail'},
        {dir: 'drawer', title: '成員編輯', page: 'MemberDetail'},
        {dir: 'drawer', title: '系統編輯', page: 'SystemDataEdit'},
        {dir: 'drawer', title: '系統設備管理', page: 'SystemEquipmentMapping'},
        {dir: 'drawer', title: '建立電費帳單', page: 'BillCreate'},
        {dir: 'drawer', title: '工單編輯', page: 'WorkOrderEdit'},
        {dir: 'drawer', title: '文件編輯', page: 'DocumentEdit'},
        {dir: 'drawer', title: '匯出工單', page: 'ExportDrawer'},
        {dir: 'drawer', title: 'AI 建立報修單', page: 'RepairOrderAssistant'},
        {dir: 'drawer', title: '收集到的資訊', page: 'CollectedInfo'},
        {dir: 'drawer', title: '設備詳情頁', page: 'EquipmentDetail'},
        {dir: 'drawer', title: '發電機詳情', page: 'GeneratorDetail'},
        {dir: 'drawer', title: '逆變器詳情', page: 'InverterDetail'},
        {dir: 'drawer', title: '監控電腦詳情', page: 'IPCDetail'},
        {dir: 'drawer', title: '落塵詳情', page: 'DustDetail'},
        {dir: 'drawer', title: '流量計詳情', page: 'WaterMeterDetail'},
        {dir: 'drawer', title: 'AI 執行歷史', page: 'AIPromptHistory'},
        {dir: 'drawer', title: 'User UI 編輯', page: 'UserUIEditor'},
        {dir: 'drawer', title: 'User UI 預覽', page: 'UserUIPreview'},
    ],
    'page': [
        {dir: 'patrol', title: '焦點案場', page: 'FocusField', meta: {"requiresAuth": true}},
        {dir: 'patrol', title: '維運流程', page: 'WorkFlows', meta: {"requiresAuth": true}},
        {dir: 'inform', title: '建立報修單', page: 'CreateIssue', meta: {"requiresAuth": true}},
        {dir: 'completed', title: '處理結果回報',page: 'CreateReport', meta: {"requiresAuth": true}},
        {dir: 'inform', title: '案場維運',page: 'FieldMaintenance', meta: {"requiresAuth": true}},
        {dir: 'patrol', title: '監控電腦列表', page: 'assetsHistory', meta: {"requiresAuth": true}},

        // 大屏展示
        {dir: 'bigscreen', title: '能源監控可視化大屏', page: 'VisualizationDashboard', meta: {"requiresAuth": true}},
        {dir: 'bigscreen', title: '收支看板', page: 'FinancialDashboard', meta: {"requiresAuth": true}},
        {dir: 'bigscreen', title: '可視化篇章', page: 'VisualizationScreen', meta: {"requiresAuth": true}},


        {dir: 'patrol', title: '複合案場狀況', page: 'VPPFieldStatus', meta: {"requiresAuth": true}},
        {dir: 'patrol', title: '光電案場狀況', page: 'PVFieldStatus', meta: {"requiresAuth": true}},
        {dir: 'patrol', title: '生質能案場狀況', page: 'BGFieldStatus', meta: {"requiresAuth": true}},
        {dir: 'patrol', title: '熱泵案場狀況', page: 'HPFieldStatus', meta: {"requiresAuth": true}},
        {dir: 'patrol', title: '儲能案場狀況', page: 'ESFieldStatus', meta: {"requiresAuth": true}},

        // 電腦、發電機、熱泵、水錶、落塵、熱泵、水錶計量
        {dir: 'manage', title: '設備總覽', page: 'EquipmentOverview', meta: {"requiresAuth": true}},
        {dir: 'data', title: '案場數據', page: 'FieldDashboard', meta: {"requiresAuth": true}},
        {dir: 'data', title: '調度', page: 'EdpData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '儲能櫃', page: 'EssData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '發電機', page: 'GenData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '逆變器', page: 'InverterData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '電錶', page: 'PowerMeterData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '錶頭', page: 'MeterStatus', meta: {"requiresAuth": true}},
        {dir: 'data', title: '熱泵', page: 'HpStatus', meta: {"requiresAuth": true}},
        {dir: 'data', title: '流量', page: 'WaterMeterStatus', meta: {"requiresAuth": true}},
        {dir: 'data', title: '落塵', page: 'DustStatus', meta: {"requiresAuth": true}},
        {dir: 'data', title: '電腦', page: 'IPCData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '溫度', page: 'TempData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '日照', page: 'PyranometerData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '流量', page: 'FlowData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '壓力', page: 'PressureData', meta: {"requiresAuth": true}},
        {dir: 'data', title: '其它', page: 'OtherData', meta: {"requiresAuth": true}},

        // 工作流程
        {dir: 'dispatch', title: '工單列表', page: 'WorkOrderList', meta: {"requiresAuth": true}},
        {dir: 'dispatch', title: '工單詳情', page: 'WorkOrderDetail', meta: {"requiresAuth": true}},
        {dir: 'dispatch', title: 'AI 建立報修單', page: 'RepairOrderAssistant', meta: {"requiresAuth": true}},
        {dir: 'dispatch', title: '工單管理', page: 'Dashboard', meta: {"requiresAuth": true}},
        {dir: 'dispatch', title: '統計分析', page: 'StatisticsPage', meta: {"requiresAuth": true}},

        // 派工單
        {dir: 'dispatch', title: '派工行事曆', page: 'DispatchCalendar', meta: {"requiresAuth": true}},

        // 報修單
        {dir: 'dispatch', title: '訊息匯整', page: 'RepairMessage', meta: {"requiresAuth": true}},

        // 定保計劃
        {dir: 'dispatch', title: '新增定保計劃', page: 'MaintainForm', meta: {"requiresAuth": true}},
        {dir: 'dispatch', title: '定保行事曆', page: 'MaintainCalendar', meta: {"requiresAuth": true}},

        // 報表
        {dir: 'report', title: '報表建立', page: 'ReportBuilder', name: 'report_ReportBuilder', meta: {"requiresAuth": true}},
        {dir: 'report', title: '報表查看', page: 'ReportViewer', name: 'report_ReportViewer', meta: {"requiresAuth": true}},
        {dir: 'report', title: '電費明細', page: 'bill', meta: {"requiresAuth": true}},
        {dir: 'report', title: '電費明細', page: 'BillDetail', name: 'report_BillDetail', meta: {"requiresAuth": true}},

        {dir: 'manage', title: '權限一覽表', page: 'PermissionDetail', meta: {"requiresAuth": true}},
        // tools 頁面
        {dir: 'tools', title: '工具列表', page: 'UserUIList', meta: {"requiresAuth": true}},
        {dir: 'tools', title: '工具渲染', page: 'UserUIRenderer', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場詳情', page: 'FieldDetail', meta: {"requiresAuth": true}},
        {dir: 'manage', title: '新增案場', page: 'CreateField', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場統計', page: 'FieldStatistics', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場帳單', page: 'FieldBillList', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場系統', page: 'FieldSystem', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場履歷', page: 'FieldResume', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場工單', page: 'FieldWorkOrderList', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場設備', page: 'FieldEquipments', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場策略', page: 'FieldEssStrategySettings', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場數據', page: 'FieldData', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場事件列表', page: 'FieldEventsList', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場履歷2', page: 'FieldEventsOld', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場圖說', page: 'FieldDrawings', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場合約', page: 'FieldContracts', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場檔案', page: 'FieldFiles', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場保養', page: 'FieldMaintenance', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場成員', page: 'FieldMember', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場分享', page: 'FieldShared', meta: {"requiresAuth": true}},
        {dir: 'field', title: '案場刪除', page: 'FieldDelete', meta: {"requiresAuth": true}},

        {dir: 'manage', title: '加入組織', page: 'OrganizationJoin', meta: {}},
        {dir: 'manage', title: '組識案場', page: 'OrganizationField', meta: {"requiresAuth": true}},
        {dir: 'manage', title: '組織成員', page: 'OrganizationMember', meta: {"requiresAuth": true}},
        {dir: 'manage', title: '組織邀請', page: 'OrganizationInvite', meta: {"requiresAuth": true}},
        {dir: 'manage', title: '組識設定', page: 'OrganizationDetail', meta: {"requiresAuth": true}},
        {dir: 'manage', title: '新增案場', page: 'OrganizationNewField', meta: {"requiresAuth": true}},

        {dir: 'manage', title: '案場資料匯入', page: 'ImportFieldData', meta: {"requiresAuth": true}},
        {dir: 'manage', title: '派工資料匯入', page: 'ImportDispatchData', meta: {"requiresAuth": true}},
        {dir: 'manage', title: '報修資料匯入', page: 'ImportRepairData', meta: {"requiresAuth": true}},
        {dir: 'manage', title: '設備資料匯入', page: 'ImportEquipmentData', meta: {"requiresAuth": true}},

        {dir: 'detail', title: 'edp-1000', page: 'detail_edp_greenshepherd_sgm_pro_1000', meta: {"requiresAuth": true}},
        {dir: 'detail', title: 'smartgen-hgm9510', page: 'detail_generator_smartgen_hgm9510', meta: {"requiresAuth": true}},
        {dir: 'detail', title: 'thnicon-gm810', page: 'detail_generator_thnicon_gm810', meta: {"requiresAuth": true}},
        {dir: 'detail', title: 'ipc_default', page: 'detail_ipc_default', meta: {"requiresAuth": true}},

        // 系統頁面
        {dir: 'manage', title: '案場選擇', page: 'FieldSelector', meta: {"requiresAuth": true}},
        {dir: 'system', title: '系統設定', page: 'SystemSettings', meta: {"requiresAuth": true}},
        {dir: 'system', title: '資料狀態', page: 'DataStatus', meta: {"requiresAuth": true}},

        {dir: 'alarm', title: '告警列表', page: 'AlarmList', meta: {"requiresAuth": true}},

        // 微前端測試
        {dir: 'admin/test', title: '微前端測試', page: 'MicroFrontendTest', meta: {"requiresAuth": true}},

        // AFU 遠端模組 (Module Federation)
        {dir: 'afu', title: 'AFU 案件管理', page: 'Cases', meta: {"requiresAuth": true}},
        {dir: 'afu', title: 'AFU 任務看板', page: 'Tasks', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '建立案件', page: 'CreateCase', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '案件詳情', page: 'CaseDetail', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '流程看板', page: 'CaseFlow', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '我的任務', page: 'MyTasks', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '合約管理', page: 'CaseContract', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '工程資料', page: 'CaseEngineering', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '設備管理', page: 'CaseEquipment', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '規劃簿', page: 'CasePlanningBook', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '變更記錄', page: 'ChangedTask', meta: {"requiresAuth": true}},
        {dir: 'afu', title: '任務排程', page: 'TaskSchedule', meta: {"requiresAuth": true}},

        // ============================================
        // om 遠端模組 (Module Federation)
        // ============================================
        // Operations - 巡檢
        {dir: 'om', title: '焦點案場', page: 'FocusField', meta: {"requiresAuth": true}},
        {dir: 'om', title: '系統看板', page: 'SystemOverview', meta: {"requiresAuth": true}},
        {dir: 'om', title: '光電案場狀況', page: 'PVFieldStatus', meta: {"requiresAuth": true}},
        {dir: 'om', title: '儲能案場狀況', page: 'ESFieldStatus', meta: {"requiresAuth": true}},
        {dir: 'om', title: '生質能案場狀況', page: 'BGFieldStatus', meta: {"requiresAuth": true}},
        {dir: 'om', title: '熱泵案場狀況', page: 'HPFieldStatus', meta: {"requiresAuth": true}},
        {dir: 'om', title: '複合案場狀況', page: 'VPPFieldStatus', meta: {"requiresAuth": true}},
        // Operations - 派工
        {dir: 'om', title: '工單列表', page: 'WorkOrderList', meta: {"requiresAuth": true}},
        {dir: 'om', title: '工單詳情', page: 'WorkOrderDetail', meta: {"requiresAuth": true}},
        {dir: 'om', title: '派工行事曆', page: 'DispatchCalendar', meta: {"requiresAuth": true}},
        {dir: 'om', title: '工作回報', page: 'WorkReport', meta: {"requiresAuth": true}},
        // Operations - 告警
        {dir: 'om', title: '告警列表', page: 'AlarmList', meta: {"requiresAuth": true}},
        // Planner - 報價
        {dir: 'om', title: '客戶管理', page: 'CustomerManagement', meta: {"requiresAuth": true}},
        {dir: 'om', title: '報價列表', page: 'QuotationList', meta: {"requiresAuth": true}},
        {dir: 'om', title: '報價工具', page: 'QuotationHome', meta: {"requiresAuth": true}},
        {dir: 'om', title: '專案管理', page: 'ProjectManagement', meta: {"requiresAuth": true}},
        // Warroom - 戰情室
        {dir: 'om', title: '戰情看板', page: 'VisualizationDashboard', meta: {"requiresAuth": true}},
        {dir: 'om', title: '風險看板', page: 'RiskDashboard', meta: {"requiresAuth": true}},
        {dir: 'om', title: '事件看板', page: 'EventsDashboard', meta: {"requiresAuth": true}},
        {dir: 'om', title: '應急響應', page: 'EmergencyDashboard', meta: {"requiresAuth": true}},
        {dir: 'om', title: '收支看板', page: 'FinancialDashboard', meta: {"requiresAuth": true}},

        // ============================================
        // hr 遠端模組 (Module Federation)
        // ============================================
        // 摘要
        {dir: 'hr', title: '人資首頁', page: 'Dashboard', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '今日出勤', page: 'OnDuty', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '我的班表', page: 'MySchedule', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '我的特休', page: 'MySpecialLeave', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '我的部門', page: 'MyDepartment', meta: {"requiresAuth": true}},
        // 表單簽核
        {dir: 'hr', title: '申請表單', page: 'FormApplication', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '待簽核', page: 'NeedSignOff', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '審核中', page: 'UnderApproval', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '已簽核', page: 'AlreadySigned', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '已駁回', page: 'AlreadyRejected', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '流程管理', page: 'ProcessManagement', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '紀錄管理', page: 'RecordManagement', meta: {"requiresAuth": true}},
        // 管理
        {dir: 'hr', title: '員工名冊', page: 'StaffList', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '員工狀態', page: 'EmployeeStatus', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '出勤管理', page: 'AttendanceStatus', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '請假管理', page: 'LeaveStatus', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '加班管理', page: 'OvertimeStatus', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '出差管理', page: 'BusinessTripStatus', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '特休時數', page: 'SpecialLeaveHours', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '排班管理', page: 'WorkScheduling', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '時間管理', page: 'TimeManagement', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '職務代理', page: 'JobAgentSettings', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '車輛借用', page: 'VehicleLoan', meta: {"requiresAuth": true}},
        // 報表
        {dir: 'hr', title: '統計報表', page: 'Summary', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '人事異動', page: 'PersonnelMovement', meta: {"requiresAuth": true}},
        // 設定
        {dir: 'hr', title: '部門設定', page: 'Department', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '新增員工', page: 'AddNewEmployees', meta: {"requiresAuth": true}},
        {dir: 'hr', title: '假日設定', page: 'HolidayWorkingDays', meta: {"requiresAuth": true}},

        // ============================================
        // essPlanner 遠端模組 (Module Federation)
        // ============================================
        {dir: 'essPlanner', title: '儲能規劃首頁', page: 'Home', meta: {"requiresAuth": true}},
        {dir: 'essPlanner', title: '專案列表', page: 'ProjectList', meta: {"requiresAuth": true}},
        {dir: 'essPlanner', title: '報價列表', page: 'QuotationList', meta: {"requiresAuth": true}},
        {dir: 'essPlanner', title: '成本設定', page: 'CostSettings', meta: {"requiresAuth": true}},
        {dir: 'essPlanner', title: '收益分析', page: 'RevenueAnalysis', meta: {"requiresAuth": true}},
        {dir: 'essPlanner', title: '表格編輯', page: 'SheetEditor', meta: {"requiresAuth": true}},
        {dir: 'essPlanner', title: '電價解析', page: 'PriceInfoParser', meta: {"requiresAuth": true}},
    ],
};

export default {
    sideNavMenus,
    pageData
};
